/**
 * Le contenu d'un chapitre : de quoi choisir, et de quoi choisir en sachant.
 *
 * Trois mentions accompagnent chaque ligne, et **aucune ne barre quoi que ce
 * soit** : déjà fait le tant, pas encore vu en classe, échelon de difficulté.
 * Le parent sait où en est la classe mieux que ce système — treize devoirs sur
 * treize étaient encore `unattached` en production le 8 septembre 2026, et la
 * frontière du programme s'ouvre aussi sur le simple découpage théorique de
 * l'année, que personne ne signe. Ce qui serait fautif n'est pas de le laisser
 * composer un chapitre non abordé, c'est de le faire sans le lui dire.
 *
 * **Le découpé est là, le corrigé non** (règle 2, affirmé côté serveur sur le
 * texte HTTP brut). Hedi, le 25 septembre 2026 : *« je veux aussi voir le
 * découpé de l'exercice pas seulement le transcript »*. Un exercice d'ouvrage
 * n'a pas de texte de notre côté — il est découpé en images —, donc « Exercice 1
 * — page 7 » et un libellé de compétence ne disent pas ce que l'exercice
 * demande : sans la vignette, le parent compose sur un numéro. L'image est
 * servie sous URL signée et de courte durée de vie (règle 8) ; le corrigé, lui,
 * attend l'écran où l'on valide.
 *
 * La ligne elle-même — mentions, vignette, case — est dans `ui/cahiers`,
 * partagée avec les cahiers de l'élève depuis le 5 octobre 2026 ; la vignette
 * dans `ui/Decoupe`, partagée avec la liste des entraînements. Elle est **hors du `<label>`** : cliquer l'image agrandit et
 * ne coche pas, le même geste ne pouvant vouloir dire deux choses.
 *
 * **« Pas encore vu en classe » se répond sur place** (Hedi, 25 septembre 2026).
 * La mention nommait un problème et ne menait nulle part : le parent — qui sait
 * où en est la classe mieux que ce système — devait quitter la V2 pour l'écran
 * « Ce qui a été vu » afin d'y répondre, et ne le faisait donc pas. Elle est
 * maintenant le déclencheur de `ui/VuEnClasse`, qui nomme la compétence, dit ce
 * que la déclaration emporte et demande confirmation **dans la page** — jamais
 * `window.confirm`, muet dans la coquille Android.
 *
 * La déclaration porte sur la **compétence** et non sur l'exercice : toutes les
 * lignes du chapitre qui la portent changent d'un coup, et celles des autres
 * cahiers aussi. D'où la copie oubliée pour l'enfant entier, puis une relecture
 * de ce chapitre — laisser la copie afficherait « pas encore vu en classe » sous
 * un bouton qui vient de dire le contraire.
 *
 * **Composer ne coûte rien et ne fait pas attendre** : tous les exercices cochés
 * sont déjà en réserve, découpés et vérifiés. Aucun modèle n'est appelé, et
 * c'est ce qui distingue la V2 du reste du système, où une compétence sans
 * stock demande une nuit de génération.
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { call } from "@/api/client";
import { ApiError } from "@/api/errors";
import type { LigneDeChapitreOut } from "@/api/schema";
import { ExerciceACocher } from "@/ui/cahiers";
import { oublierLesOuvrages, useContenuDOuvrage } from "@/ui/enCache";
import { VuEnClasse } from "@/ui/VuEnClasse";
import { etiquette } from "@/ui/matieres";
import { Button, Card, Empty, ErrorNote, Loading, Screen } from "@/ui/primitives";
import { entrainementsDe } from "@/v2/chemins";

/**
 * Une ligne du chapitre, côté parent : la ligne commune (`ui/cahiers`), dont
 * la mention « pas encore vu en classe » est le déclencheur de `ui/VuEnClasse`.
 */
function Ligne({
  ligne,
  childId,
  cochee,
  basculer,
  declaree,
}: {
  ligne: LigneDeChapitreOut;
  childId: string;
  cochee: boolean;
  basculer: () => void;
  declaree: () => void | Promise<void>;
}) {
  return (
    <ExerciceACocher
      ligne={ligne}
      cochee={cochee}
      basculer={basculer}
      pasEncoreVu={
        <VuEnClasse
          childId={childId}
          competencyId={ligne.competency_id}
          libelle={ligne.competence}
          intitule="pas encore vu en classe"
          discret
          onDeclare={declaree}
        />
      }
    />
  );
}

export function ChapitreScreen() {
  const { childId = "", ouvrage = "", cle = "" } = useParams();
  const navigate = useNavigate();
  // Les énoncés découpés d'un cahier acheté ne changent pas : gardés dans le
  // navigateur le temps de la session (`ui/enCache`). Ce que composer rend
  // faux — le compte des exercices déjà faits — est oublié en composant.
  const {
    donnees: chapitre,
    erreur: erreurDeCharge,
    recharger,
  } = useContenuDOuvrage("parascolaireChapitre", {
    params: { child_id: childId, ouvrage, cle },
  });
  const [erreurDEnvoi, setErreurDEnvoi] = useState<string | null>(null);
  const erreur = erreurDEnvoi ?? erreurDeCharge;
  const [cochees, setCochees] = useState<string[]>([]);
  const [envoi, setEnvoi] = useState(false);
  const choisies = useMemo(() => new Set(cochees), [cochees]);
  // Changer de chapitre remet les cases à zéro. L'effet ne charge plus rien —
  // le cache s'en occupe — mais la sélection, elle, appartient au chapitre
  // qu'on quitte : la reporter sur le suivant ferait composer autre chose que
  // ce qui est coché à l'écran.
  useEffect(() => {
    setCochees([]);
    setErreurDEnvoi(null);
  }, [childId, ouvrage, cle]);

  const lignes = chapitre?.lignes ?? [];

  const basculer = (exerciseId: string) =>
    setCochees((avant) =>
      avant.includes(exerciseId)
        ? avant.filter((autre) => autre !== exerciseId)
        : [...avant, exerciseId],
    );

  /**
   * Une compétence déclarée vue en classe : oublier, puis relire.
   *
   * La copie est oubliée pour **l'enfant entier** et non pour ce chapitre : la
   * déclaration porte sur une compétence, et le même chapitre d'un autre cahier
   * — ou un autre chapitre du même — peut la porter aussi.
   */
  const declaree = () => {
    oublierLesOuvrages(childId);
    recharger();
  };

  /**
   * **L'ordre envoyé n'est pas celui qui compte.** Le serveur range la série
   * dans l'ordre imprimé, quel que soit l'ordre des cases cochées : le parent
   * coche dans l'ordre où son œil descend la page, et ce que l'enfant a sous les
   * yeux est le cahier ouvert.
   */
  const envoyer = async () => {
    setEnvoi(true);
    setErreurDEnvoi(null);
    try {
      await call("composerUnEntrainement", {
        params: { child_id: childId, ouvrage, cle },
        body: { exercise_ids: cochees },
      });
      // Le compte des exercices déjà faits vient de changer sur le catalogue et
      // sur le sommaire : garder la copie afficherait au parent le contraire de
      // ce qu'il vient de faire.
      oublierLesOuvrages(childId);
      navigate(`${entrainementsDe(childId)}/composes`);
    } catch (echec) {
      setErreurDEnvoi(echec instanceof ApiError ? echec.message : "L'envoi a échoué.");
      setEnvoi(false);
    }
  };

  return (
    <Screen
      title={chapitre?.titre ?? "Le chapitre"}
      lead={
        chapitre
          ? `${chapitre.ouvrage_titre} — ${etiquette(chapitre.matiere, chapitre.matiere_libelle)}` +
            (chapitre.page ? ` — page ${chapitre.page}` : "")
          : undefined
      }
    >
      <p className="notice">
        <Link to={`${entrainementsDe(childId)}/${ouvrage}`}>← Le sommaire</Link>
      </p>

      {erreur ? <ErrorNote>{erreur}</ErrorNote> : null}
      {!chapitre && !erreur ? <Loading /> : null}

      {chapitre && lignes.length === 0 ? (
        <Card>
          <Empty>Ce chapitre n'a aucun exercice servable pour cet enfant.</Empty>
        </Card>
      ) : null}

      {lignes.length > 0 ? (
        <Card>
          <ul className="list">
            {lignes.map((ligne) => (
              <Ligne
                key={ligne.exercise_id}
                ligne={ligne}
                childId={childId}
                cochee={choisies.has(ligne.exercise_id)}
                basculer={() => basculer(ligne.exercise_id)}
                declaree={declaree}
              />
            ))}
          </ul>
          <div className="screen__actions">
            <Button type="button" disabled={cochees.length === 0 || envoi} onClick={envoyer}>
              {envoi
                ? "Envoi…"
                : `Ajouter ${cochees.length} exercice${cochees.length > 1 ? "s" : ""} à la feuille`}
            </Button>
            {/* « Ajouter » et non « Préparer » : depuis le 25 septembre 2026 la
                composition **complète la feuille en cours** au lieu d'en ouvrir
                une, et c'est clore qui envoie. Un bouton qui dit « préparer »
                laisserait croire que l'enfant vient de recevoir quelque chose,
                et la feuille resterait ouverte sans que rien ne le dise. */}
            <p className="muted small">
              Ils s'ajoutent à la feuille en cours, que l'enfant ne voit pas encore. Vous pouvez y
              mettre d'autres chapitres, puis la clôturer : c'est ce qui la lui envoie.
            </p>
          </div>
        </Card>
      ) : null}
    </Screen>
  );
}
